import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="analytics--metric-card"
export default class extends Controller {
  static targets = ["value", "label", "change", "loading", "error", "icon"];
  static values = {
    drillDownType: String,
    drillDownValue: String,
    metric: Object,
    sectionType: String,
    animated: Boolean,
  };

  connect() {
    this.initializeCard();
    this.setupEventListeners();
  }

  disconnect() {
    this.cleanup();
  }

  // Initialize metric card with data and interactions
  initializeCard() {
    this.updateDisplay();
    this.setupInteractivity();
    this.setupAccessibility();
  }

  // Set up event listeners
  setupEventListeners() {
    this.element.addEventListener(
      "analytics:sectionDataUpdated",
      this.handleDataUpdate.bind(this),
    );
    this.element.addEventListener(
      "mouseenter",
      this.handleMouseEnter.bind(this),
    );
    this.element.addEventListener(
      "mouseleave",
      this.handleMouseLeave.bind(this),
    );
    this.element.addEventListener("keydown", this.handleKeyDown.bind(this));
  }

  // Clean up event listeners
  cleanup() {
    this.element.removeEventListener(
      "analytics:sectionDataUpdated",
      this.handleDataUpdate.bind(this),
    );
    this.element.removeEventListener(
      "mouseenter",
      this.handleMouseEnter.bind(this),
    );
    this.element.removeEventListener(
      "mouseleave",
      this.handleMouseLeave.bind(this),
    );
    this.element.removeEventListener("keydown", this.handleKeyDown.bind(this));
  }

  // Set up card interactivity
  setupInteractivity() {
    if (this.hasDrillDownTypeValue) {
      this.element.classList.add(
        "cursor-pointer",
        "transition-all",
        "duration-200",
      );
      this.element.setAttribute("role", "button");
      this.element.setAttribute("tabindex", "0");

      // Add hover effects
      this.element.classList.add("hover:shadow-pop", "hover:scale-105");
    }
  }

  // Set up accessibility attributes
  setupAccessibility() {
    if (this.hasDrillDownTypeValue) {
      this.element.setAttribute(
        "aria-label",
        `${this.getLabelText()} - Click to view details`,
      );
      this.element.setAttribute(
        "aria-describedby",
        `metric-${this.drillDownTypeValue}-description`,
      );
    }
  }

  // Handle metric card click for drill-down
  handleClick(event) {
    if (!this.hasDrillDownTypeValue) return;

    event.preventDefault();

    // Add click animation
    this.addClickAnimation();

    // Trigger drill-down
    this.triggerDrillDown();
  }

  // Handle keyboard navigation
  handleKeyDown(event) {
    if (!this.hasDrillDownTypeValue) return;

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.handleClick(event);
    }
  }

  // Handle mouse enter for hover effects
  handleMouseEnter() {
    if (!this.hasDrillDownTypeValue) return;

    this.element.classList.add("shadow-pop", "scale-105");

    // Show tooltip if available
    this.showTooltip();
  }

  // Handle mouse leave
  handleMouseLeave() {
    if (!this.hasDrillDownTypeValue) return;

    this.element.classList.remove("shadow-pop", "scale-105");

    // Hide tooltip
    this.hideTooltip();
  }

  // Add click animation
  addClickAnimation() {
    this.element.classList.add("scale-95");

    setTimeout(() => {
      this.element.classList.remove("scale-95");
    }, 150);
  }

  // Trigger drill-down functionality
  triggerDrillDown() {
    const drillDownEvent = new CustomEvent("analytics:drillDownRequested", {
      detail: {
        type: this.drillDownTypeValue,
        value: this.drillDownValueValue,
        section: this.sectionTypeValue || "party_overview",
        title: this.getLabelText(),
        metric: this.metricValue,
        filters: this.buildDrillDownFilters(),
      },
      bubbles: true,
    });

    this.element.dispatchEvent(drillDownEvent);
  }

  // Build filters for drill-down based on metric type
  buildDrillDownFilters() {
    const filters = {};

    switch (this.drillDownTypeValue) {
      case "party_status":
        filters.status = this.drillDownValueValue;
        break;
      case "party_type":
        filters.party_type = this.drillDownValueValue;
        break;
      case "financial_category":
        filters.category = this.drillDownValueValue;
        break;
      case "guest_engagement":
        filters.engagement_level = this.drillDownValueValue;
        break;
      case "timeline_status":
        filters.task_status = this.drillDownValueValue;
        break;
      case "product_category":
        filters.product_category = this.drillDownValueValue;
        break;
      default:
        filters.filter_value = this.drillDownValueValue;
    }

    return filters;
  }

  // Handle data updates from dashboard controller
  handleDataUpdate(event) {
    const { sectionType, data } = event.detail;

    if (sectionType === this.sectionTypeValue && data) {
      this.updateMetricFromData(data);
    }
  }

  // Update metric from section data
  updateMetricFromData(sectionData) {
    // Extract relevant metric based on drill-down type
    let newMetric = null;

    switch (this.drillDownTypeValue) {
      case "total_parties":
        newMetric = {
          value: sectionData.total_parties,
          change: sectionData.party_change,
        };
        break;
      case "upcoming_parties":
        newMetric = {
          value: sectionData.upcoming_parties,
          change: sectionData.upcoming_change,
        };
        break;
      case "total_guests":
        newMetric = {
          value: sectionData.total_guests,
          change: sectionData.guest_change,
        };
        break;
      case "response_rate":
        newMetric = {
          value: `${(sectionData.rsvp_response_rate * 100).toFixed(1)}%`,
          change: sectionData.response_rate_change,
        };
        break;
      case "total_spending":
        newMetric = {
          value: this.formatCurrency(sectionData.total_spending),
          change: sectionData.spending_change,
        };
        break;
      case "completion_rate":
        newMetric = {
          value: `${(sectionData.completion_rate * 100).toFixed(1)}%`,
          change: sectionData.completion_change,
        };
        break;
      default:
        // Try to find metric in data by key
        if (sectionData[this.drillDownTypeValue]) {
          newMetric = { value: sectionData[this.drillDownTypeValue] };
        }
    }

    if (newMetric) {
      this.updateMetric(newMetric);
    }
  }

  // Update metric display with new data
  updateMetric(newMetric) {
    if (!newMetric) return;

    const oldValue = this.getValueText();
    const newValue = newMetric.value;

    // Update metric value
    this.metricValue = { ...this.metricValue, ...newMetric };

    // Animate value change if enabled
    if (this.animatedValue && oldValue !== newValue) {
      this.animateValueChange(oldValue, newValue);
    } else {
      this.updateDisplay();
    }

    // Update change indicator
    if (newMetric.change !== undefined) {
      this.updateChangeIndicator(newMetric.change);
    }
  }

  // Update the display with current metric data
  updateDisplay() {
    const metric = this.metricValue || {};

    // Update value
    if (this.hasValueTarget) {
      this.valueTarget.textContent = metric.value || "0";
    }

    // Update label
    if (this.hasLabelTarget && metric.label) {
      this.labelTarget.textContent = metric.label;
    }

    // Update icon
    if (this.hasIconTarget && metric.icon) {
      this.iconTarget.className = `${metric.icon} w-6 h-6`;
    }
  }

  // Animate value change
  animateValueChange(oldValue, newValue) {
    if (!this.hasValueTarget) return;

    // If both values are numbers, animate the counting
    const oldNum = parseFloat(oldValue.replace(/[^0-9.-]/g, ""));
    const newNum = parseFloat(newValue.replace(/[^0-9.-]/g, ""));

    if (!isNaN(oldNum) && !isNaN(newNum)) {
      this.animateNumberChange(oldNum, newNum, newValue);
    } else {
      // Simple fade animation for non-numeric values
      this.animateFadeChange(newValue);
    }
  }

  // Animate number counting
  animateNumberChange(startNum, endNum, finalValue) {
    const duration = 1000; // 1 second
    const startTime = Date.now();
    const difference = endNum - startNum;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentNum = startNum + difference * easeOut;

      // Format the current number
      const formattedValue = this.formatAnimatedNumber(currentNum, finalValue);
      this.valueTarget.textContent = formattedValue;

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.valueTarget.textContent = finalValue;
      }
    };

    animate();
  }

  // Animate fade change for non-numeric values
  animateFadeChange(newValue) {
    this.valueTarget.style.opacity = "0.5";

    setTimeout(() => {
      this.valueTarget.textContent = newValue;
      this.valueTarget.style.opacity = "1";
    }, 200);
  }

  // Format animated number to match final format
  formatAnimatedNumber(currentNum, finalValue) {
    if (finalValue.includes("%")) {
      return `${currentNum.toFixed(1)}%`;
    } else if (finalValue.includes("$")) {
      return this.formatCurrency(currentNum);
    } else if (finalValue.includes(",")) {
      return Math.round(currentNum).toLocaleString();
    } else {
      return Math.round(currentNum).toString();
    }
  }

  // Update change indicator (positive/negative change)
  updateChangeIndicator(change) {
    if (!this.hasChangeTarget || change === undefined) return;

    const changeValue = parseFloat(change);
    const isPositive = changeValue > 0;
    const isNegative = changeValue < 0;

    // Update text
    const prefix = isPositive ? "+" : "";
    const suffix =
      typeof change === "string" && change.includes("%") ? "%" : "";
    this.changeTarget.textContent = `${prefix}${changeValue}${suffix}`;

    // Update styling
    this.changeTarget.classList.remove(
      "text-green-600",
      "text-red-600",
      "text-gray-500",
    );

    if (isPositive) {
      this.changeTarget.classList.add("text-green-600");
    } else if (isNegative) {
      this.changeTarget.classList.add("text-red-600");
    } else {
      this.changeTarget.classList.add("text-gray-500");
    }
  }

  // Show loading state
  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("hidden");
    }

    if (this.hasValueTarget) {
      this.valueTarget.style.opacity = "0.5";
    }

    this.element.classList.add("pointer-events-none");
  }

  // Hide loading state
  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("hidden");
    }

    if (this.hasValueTarget) {
      this.valueTarget.style.opacity = "1";
    }

    this.element.classList.remove("pointer-events-none");
  }

  // Show error state
  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message;
      this.errorTarget.classList.remove("hidden");
    }

    this.element.classList.add("border-red-300", "bg-red-50");
  }

  // Hide error state
  hideError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add("hidden");
    }

    this.element.classList.remove("border-red-300", "bg-red-50");
  }

  // Show tooltip
  showTooltip() {
    const tooltip = this.element.querySelector("[data-tooltip]");
    if (tooltip) {
      tooltip.classList.remove("hidden");
    }
  }

  // Hide tooltip
  hideTooltip() {
    const tooltip = this.element.querySelector("[data-tooltip]");
    if (tooltip) {
      tooltip.classList.add("hidden");
    }
  }

  // Get current label text
  getLabelText() {
    if (this.hasLabelTarget) {
      return this.labelTarget.textContent;
    }
    return this.metricValue?.label || "Metric";
  }

  // Get current value text
  getValueText() {
    if (this.hasValueTarget) {
      return this.valueTarget.textContent;
    }
    return this.metricValue?.value || "0";
  }

  // Format currency values
  formatCurrency(value) {
    const num = parseFloat(value);
    if (isNaN(num)) return "$0";

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  }

  // Format percentage values
  formatPercentage(value) {
    const num = parseFloat(value);
    if (isNaN(num)) return "0%";

    return `${(num * 100).toFixed(1)}%`;
  }

  // Format large numbers with abbreviations
  formatLargeNumber(value) {
    const num = parseFloat(value);
    if (isNaN(num)) return "0";

    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    } else {
      return num.toLocaleString();
    }
  }
}
